import React, { useMemo, useState } from "react";
import { Tag, Button, Space, message, Modal, Input, Form } from "antd";
import { useHistory } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useTranslation } from "react-i18next";

import { Card } from "misc/card";
import { clientUrls } from "misc/urls";
import { TextField } from "misc/fields";
import { EditableTable } from "misc/tables";
import { buildUrl } from "misc/helpfunctions";
import { useDeleteRole, useRoles, useApps, useCreateRole } from "misc/api/keycloakApi";
import FormItem from "./steps/FormItem";

const RolesListComponent = () => {
    const history = useHistory();

    const [t] = useTranslation();

    const { apps } = useApps();

    const { roles, isLoading } = useRoles({ suspense: true, clients: apps });

    // Filter roles based on apps
    const filteredRoles = useMemo(() => {
        return roles.filter(role => apps.includes(role.client));
    }, [roles, apps]);

    const { deleteRole, isLoading: isDeleting } = useDeleteRole();
    const { createRole, isLoading: isCreating } = useCreateRole();

    const [duplicateModalVisible, setDuplicateModalVisible] = useState(false);
    const [selectedRole, setSelectedRole] = useState(null);

    const [duplicateForm] = Form.useForm();

    const handleDuplicateRole = role => {
        setSelectedRole(role);
        setDuplicateModalVisible(true);
    };

    const handleDuplicateConfirm = () => {
        duplicateForm.validateFields().then(values => {
            const payload = {
                client: selectedRole.client,
                description: values.description,
                name: values.name,
                permissions: selectedRole.permissions.map(p => p.id)
            };

            setDuplicateModalVisible(false);
            duplicateForm.resetFields();

            createRole(payload, {
                onSuccess: () => {
                    message.success(t("t_roles_duplicate_success"), 1, () => {
                        history.push(clientUrls.roles.baseUrl());
                    });
                },
                onError: error => {
                    message.error(error.error ? error.error : t(`t_http_error_${error.status}`));
                }
            });
        });
    };

    const columns = useMemo(() => {
        return [
            {
                title: <TextField value="t_roles_list_name" />,
                key: "name",
                dataIndex: "name",
                width: 200,
                render: text => text.replace("role_", ""),
                sorter: (a, b) => a.name.localeCompare(b.name),
                defaultSortOrder: "ascend"
            },
            {
                title: <TextField value="t_roles_list_app" />,
                key: "app",
                dataIndex: "client",
                width: 150,
                render: text => <Tag>{text}</Tag>,
                filterSearch: true,
                filters: apps.map(app => ({
                    text: app,
                    value: app
                })),
                onFilter: (value, record) => record.client === value
            },
            {
                title: <TextField value="t_roles_list_description" />,
                key: "description",
                dataIndex: "description",
                width: 300
            }
        ];
    }, [apps]);

    const onDeleteRole = role => {
        deleteRole(role, {
            onSuccess: () => {
                message.success(t("t_roles_delete_success"), 1, () => history.push(clientUrls.roles.baseUrl()));
            },
            onError: error => {
                message.error(error.error ? error.error : t(`t_http_error_${error.status}`));
            }
        });
    };

    return (
        <>
            <Card title="t_roles_list_title" height={700} stretchHeight>
                <EditableTable
                    dataSource={filteredRoles.map(role => ({ ...role, editable: true, deletable: true }))}
                    columns={columns}
                    rowKey="id"
                    onDeleteItem={onDeleteRole}
                    extraActions={record => [
                        {
                            disabled: !record.editable,
                            tooltip: "t_edit",
                            key: `edit ${record.id}`,
                            icon: <FontAwesomeIcon icon={["fal", "pen-to-square"]} />,
                            onClick: () =>
                                history.push(
                                    buildUrl({
                                        path: clientUrls.roles.edit(),
                                        queryParameters: { roleId: record.id }
                                    })
                                )
                        },
                        {
                            tooltip: "t_duplicate",
                            key: `duplicate ${record.id}`,
                            icon: <FontAwesomeIcon icon={["fal", "copy"]} />,
                            onClick: () => handleDuplicateRole(record)
                        }
                    ]}
                    extraHeader={
                        <Button onClick={() => history.push(clientUrls.roles.create())}>
                            <Space>
                                <FontAwesomeIcon icon={["fal", "plus"]} />
                                <TextField value="t_roles_create_title" />
                            </Space>
                        </Button>
                    }
                    isLoading={isLoading || isDeleting || isCreating}
                />
            </Card>

            <Modal
                title={
                    <Space>
                        <TextField value="t_roles_duplicate_title" />
                        {selectedRole && <span style={{ color: "#8c8c8c" }}>({selectedRole.name.replace("role_", "")})</span>}
                    </Space>
                }
                open={duplicateModalVisible}
                onCancel={() => {
                    setDuplicateModalVisible(false);
                    duplicateForm.resetFields();
                }}
                onOk={handleDuplicateConfirm}
                styles={{
                    body: {
                        paddingTop: 12
                    }
                }}
            >
                <Form form={duplicateForm}>
                    <Space direction="vertical" size={8} style={{ width: "100%" }}>
                        <FormItem
                            form={duplicateForm}
                            name="name"
                            title="t_roles_create_form_name"
                            icon="Aa"
                            rules={[
                                {
                                    required: true,
                                    message: t("t_form_required_field")
                                },
                                {
                                    message: t("t_roles_name_already_taken"),
                                    validator: (_, currentValue) => {
                                        return roles.some(
                                            role => role.name.replace("role_", "") === currentValue && role?.client === selectedRole?.client
                                        )
                                            ? Promise.reject()
                                            : Promise.resolve();
                                    }
                                }
                            ]}
                        >
                            <Input placeholder={selectedRole ? selectedRole.name.replace("role_", "") : t("t_roles_create_form_name_placeholder")} />
                        </FormItem>

                        <FormItem
                            form={duplicateForm}
                            name="description"
                            title="t_roles_create_form_description"
                            icon={<FontAwesomeIcon icon={["fas", "pen-field"]} />}
                            rules={[
                                {
                                    required: true,
                                    message: t("t_form_required_field")
                                }
                            ]}
                        >
                            <Input placeholder={selectedRole ? selectedRole.description : t("t_roles_create_form_description_placeholder")} />
                        </FormItem>
                    </Space>
                </Form>
            </Modal>
        </>
    );
};

export default RolesListComponent;
